import { formatMoney, type Cents } from '@paybae/shared'

import { cn } from '@/lib/cn'

/**
 * El número héroe.
 *
 * Va en tinta, no en verde. Casi toda app financiera colorea el saldo según sea
 * «bueno» o «malo», y eso obliga a leer un juicio antes que un dato: entrena
 * ansiedad cada vez que se abre la app. Aquí el color solo aparece cuando pasa
 * algo que de verdad requiere atención — un sobregiro real —, así que ver el
 * número en tinta ya es, en sí mismo, la señal de que todo está en orden.
 */
export function MoneyHero({
  cents,
  label,
  caption,
  className,
}: {
  cents: Cents
  label: string
  caption?: string
  className?: string
}) {
  const overdrawn = cents < 0
  const formatted = formatMoney(cents, { hideZeroCents: true })

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <span className="text-[13px] font-medium uppercase tracking-[0.08em] text-ink-soft">
        {label}
      </span>
      <span
        data-money
        className={cn(
          'font-display text-[clamp(2.75rem,13vw,4rem)] font-extrabold leading-[0.95] tracking-[-0.04em]',
          overdrawn ? 'text-expense' : 'text-ink',
        )}
      >
        {formatted}
      </span>
      {caption ? <span className="text-sm text-ink-soft">{caption}</span> : null}
    </div>
  )
}

/** Monto en línea, para filas de lista y detalles. */
export function Money({
  cents,
  className,
  showSign = false,
  hideZeroCents = true,
  tone = 'ink',
}: {
  cents: Cents
  className?: string
  showSign?: boolean
  hideZeroCents?: boolean
  tone?: 'ink' | 'soft' | 'income' | 'expense' | 'primary'
}) {
  // Las variantes `-text` y no el color pleno: este monto siempre va en una
  // fila sobre papel o sobre un tinte, y ahí el verde pleno (#12b886) daba
  // 2.4:1 y el coral 3.1:1, los dos por debajo del 4.5:1 que pide un texto de
  // este cuerpo. El color pleno queda para rellenos: arcos, puntos, barras.
  const tones = {
    ink: 'text-ink',
    soft: 'text-ink-soft',
    income: 'text-income-text',
    expense: 'text-expense-text',
    primary: 'text-primary-text',
  } as const

  return (
    <span data-money className={cn('font-medium', tones[tone], className)}>
      {formatMoney(cents, { showSign, hideZeroCents })}
    </span>
  )
}
